import React, { useEffect, useMemo, useState } from "react";

import { Canal, Catalogo, DetalleTipo, NOMBRE_CANAL, TipoCatalogo, VistaPrevia, avisosApi, cuando, errorTexto } from "./avisos.service";
import "./avisos.css";

const CANALES: Canal[] = ["wapp", "push", "wappteam", "correo"];
const PRIORIDAD: Record<string, string> = { urgente: "Urgente", normal: "Normal", informativo: "Informativo" };
const ALCANCE: Record<string, string> = { plantel: "Del plantel del evento", territorio: "Del territorio del evento", todos: "De todos los planteles" };

/**
 * Configurar Avisos (Acceso especial, solo Super Admin; Esteban 2026-10-10): el catálogo de avisos, quién recibe cada uno
 * (rol × alcance, papeles del evento, personas fijas, correos, exclusiones), canales y modo por defecto, y
 * "¿quién lo recibiría?" eligiendo plantel.
 */
const ConfigurarAvisos: React.FC = () => {
	const [cat, setCat] = useState<Catalogo | null>(null);
	const [sel, setSel] = useState<number | null>(null);
	const [buscar, setBuscar] = useState("");
	const [error, setError] = useState<string | null>(null);

	const cargar = () => avisosApi.catalogo().then((r) => setCat(r.data)).catch((e) => setError(errorTexto(e)));
	useEffect(() => { cargar(); }, []);

	const grupos = useMemo(() => {
		const q = buscar.trim().toLowerCase();
		const m: Record<string, TipoCatalogo[]> = {};
		(cat?.tipos || []).filter((t) => !q || (t.nombre + " " + t.code).toLowerCase().includes(q)).forEach((t) => { (m[t.grupo] = m[t.grupo] || []).push(t); });
		return m;
	}, [cat, buscar]);

	if (!cat) return <div className="avisos-pag">{error ? <div className="av-aviso oro">{error}</div> : <div className="loader" />}</div>;
	return (
		<div className="avisos-pag av-config">
			<div className="av-cab">
				<h1>Configurar Avisos</h1>
				<input className="av-buscar" placeholder="Buscar aviso…" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
			</div>
			{error ? <div className="av-aviso oro" onClick={() => setError(null)}>{error}</div> : null}
			<div className={"avk" + (sel ? " con-detalle" : "")}>
				<div className="avk-lista">
					{Object.entries(grupos).map(([g, tipos]) => (
						<section key={g} className="avk-grupo">
							<h2>{g}</h2>
							{tipos.map((t) => (
								<button key={t.id} className={"avk-tipo" + (sel === t.id ? " on" : "") + (!t.activo ? " apagado" : "")} onClick={() => setSel(t.id)}>
									<span className="avk-tipo-nom">
										<b>{t.nombre}</b>
										<small>{t.configurable ? (t.canales_defecto.map((c) => NOMBRE_CANAL[c]).join(" · ") || "Sin canal") : "Del sistema"}
											{t.reglas ? " · " + t.reglas + (t.reglas === 1 ? " regla" : " reglas") : ""}</small>
									</span>
									<span className="avk-tipo-cifras">
										{t.prioridad === "urgente" ? <em>Urgente</em> : null}
										{t.fallas ? <em className="oro">{t.fallas} fallas</em> : null}
										{t.semana ? <span title="Avisos en la bandeja en los últimos 7 días">{t.semana}</span> : null}
									</span>
								</button>
							))}
						</section>
					))}
				</div>
				{sel ? <DetalleTipoPanel key={sel} id={sel} cat={cat} onCerrar={() => setSel(null)} onCambio={cargar} /> : (
					<div className="avk-detalle avk-nada">Elige un aviso para ver quién lo recibe.</div>
				)}
			</div>
		</div>
	);
};

const DetalleTipoPanel: React.FC<{ id: number; cat: Catalogo; onCerrar: () => void; onCambio: () => void }> = ({ id, cat, onCerrar, onCambio }) => {
	const [d, setD] = useState<DetalleTipo | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [ok, setOk] = useState<string | null>(null);
	const [plantel, setPlantel] = useState<number | null>(cat.planteles[0]?.id ?? null);
	const [pais, setPais] = useState<string | null>(null);
	const [vista, setVista] = useState<VistaPrevia | null>(null);
	const [nueva, setNueva] = useState<any>({ clase: "rol", alcance: "plantel" });
	const [personas, setPersonas] = useState<{ id: number; nombre: string; correo: string }[]>([]);
	const [ocupado, setOcupado] = useState(false);

	useEffect(() => { avisosApi.tipo(id).then((r) => setD(r.data)).catch((e) => setError(errorTexto(e))); }, [id]);
	const verQuien = () => { setVista(null); avisosApi.vista(id, plantel, pais).then((r) => setVista(r.data)).catch((e) => setError(errorTexto(e))); };
	useEffect(() => { if (d) verQuien(); }, [d, plantel, pais]);
	useEffect(() => {
		if (nueva.clase !== "usuario" || (nueva.q || "").trim().length < 2) { setPersonas([]); return; }
		const t = setTimeout(() => avisosApi.personas(nueva.q).then((r) => setPersonas(r.data)).catch(() => setPersonas([])), 250);
		return () => clearTimeout(t);
	}, [nueva.clase, nueva.q]);

	const hacer = async (fn: () => Promise<any>, msg?: string) => {
		setOcupado(true); setError(null); setOk(null);
		try { const r = await fn(); if (r?.data?.tipo) setD(r.data); if (msg) setOk(msg); onCambio(); }
		catch (e) { setError(errorTexto(e)); } finally { setOcupado(false); }
	};

	if (!d) return <div className="avk-detalle">{error ? <div className="av-aviso oro">{error}</div> : <div className="loader" />}</div>;
	const t = d.tipo;
	const editable = t.configurable;
	const agregar = () => hacer(() => avisosApi.agregarRegla(id, { ...nueva, q: undefined }), "Regla agregada.").then(() => setNueva({ clase: nueva.clase, alcance: "plantel" }));
	const prueba = () => hacer(async () => {
		const r = await avisosApi.prueba(id);
		const c = r.data.resultado?.canales || {};
		setOk(r.data.resultado ? "Te llegó: WAPP" + Object.entries(c).map(([k, v]) => " · " + (NOMBRE_CANAL as any)[k] + " " + v).join("") : "No salió: el aviso está apagado.");
		return r;
	});

	return (
		<div className="avk-detalle">
			<div className="avk-det-cab">
				<div>
					<h2>{t.nombre}</h2>
					<small>{t.grupo}{t.inventario ? " · #" + t.inventario + " del inventario" : ""} · {t.code}</small>
				</div>
				<button className="av-x grande" aria-label="Cerrar" onClick={onCerrar}>×</button>
			</div>
			{t.descripcion ? <p className="avk-desc">{t.descripcion}</p> : null}
			{error ? <div className="av-aviso oro" onClick={() => setError(null)}>{error}</div> : null}
			{ok ? <div className="av-aviso ok" onClick={() => setOk(null)}>{ok}</div> : null}

			{editable ? (
				<section className="avk-bloque">
					<h3>Cómo Sale</h3>
					<div className="avk-form">
						<label>Prioridad
							<select value={t.prioridad} disabled={ocupado} onChange={(e) => hacer(() => avisosApi.guardarTipo(id, { prioridad: e.target.value }))}>
								{Object.entries(PRIORIDAD).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
							</select>
						</label>
						<label>Cuándo
							<select value={t.modo_defecto} disabled={ocupado || !t.bandeja} onChange={(e) => hacer(() => avisosApi.guardarTipo(id, { modo_defecto: e.target.value }))}>
								<option value="inmediato">Al Momento</option><option value="resumen">En el Resumen</option>
							</select>
						</label>
						{t.intervalo_min ? (
							<label>Cada (min)
								<input type="number" min={5} max={240} defaultValue={t.intervalo_min} disabled={ocupado}
									onBlur={(e) => Number(e.target.value) !== t.intervalo_min && hacer(() => avisosApi.guardarTipo(id, { intervalo_min: Number(e.target.value) }))} />
							</label>
						) : null}
						<label className="av-switch">
							<input type="checkbox" checked={t.activo} disabled={ocupado} onChange={(e) => hacer(() => avisosApi.guardarTipo(id, { activo: e.target.checked }))} />
							<span>{t.activo ? "Activo" : "Apagado para todos"}</span>
						</label>
					</div>
					<div className="avk-canales">
						<span className="av-etq">Canales por defecto</span>
						{CANALES.map((c) => {
							const permitido = t.canales_permitidos.includes(c);
							const on = t.canales_defecto.includes(c) || (c === "wapp" && t.bandeja);
							return (
								<button key={c} className={"av-canal" + (on ? " on" : "") + (!permitido ? " no" : "")} aria-pressed={on}
									disabled={!permitido || (c === "wapp" && t.bandeja) || ocupado}
									onClick={() => hacer(() => avisosApi.guardarTipo(id, { canales_defecto: on ? t.canales_defecto.filter((x) => x !== c) : [...t.canales_defecto, c] }))}>
									{NOMBRE_CANAL[c]}
								</button>
							);
						})}
					</div>
				</section>
			) : <p className="avk-desc">Aviso del sistema: no se configura aquí.</p>}

			<section className="avk-bloque">
				<h3>Quién lo Recibe</h3>
				{d.reglas.length === 0 ? <div className="av-tenue">Sin reglas: no le llega a nadie.</div> : (
					<ul className="avk-reglas">
						{d.reglas.map((r) => (
							<li key={r.id} className={r.excluir ? "excluir" : ""}>
								<span className="avk-regla-txt"><b>{r.excluir ? "No: " : ""}{r.texto}</b>{r.nota ? <small>{r.nota}</small> : null}</span>
								{editable ? <button className="av-x" aria-label="Quitar regla" disabled={ocupado} onClick={() => hacer(() => avisosApi.quitarRegla(r.id))}>×</button> : null}
							</li>
						))}
					</ul>
				)}
				{editable ? (
					<div className="avk-nueva">
						<select value={nueva.clase} onChange={(e) => setNueva({ clase: e.target.value, alcance: "plantel" })} aria-label="Tipo de regla">
							<option value="rol">Por rol</option>
							<option value="relacion">Papel en el evento</option>
							<option value="usuario">Persona</option>
							<option value="correo">Correo de fuera</option>
						</select>
						{nueva.clase === "rol" ? (
							<>
								<select value={nueva.role_id ?? ""} onChange={(e) => setNueva({ ...nueva, role_id: Number(e.target.value) })} aria-label="Rol">
									<option value="">Elige rol…</option>
									{cat.roles.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
								</select>
								<select value={nueva.alcance} onChange={(e) => setNueva({ ...nueva, alcance: e.target.value })} aria-label="Alcance">
									{Object.entries(ALCANCE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
								</select>
							</>
						) : nueva.clase === "relacion" ? (
							<select value={nueva.relacion ?? ""} onChange={(e) => setNueva({ ...nueva, relacion: e.target.value })} aria-label="Papel">
								<option value="">Elige papel…</option>
								{d.relaciones.map((r) => <option key={r.code} value={r.code}>{r.nombre}</option>)}
							</select>
						) : nueva.clase === "usuario" ? (
							<div className="avk-persona">
								<input placeholder="Nombre o correo…" value={nueva.q ?? ""} onChange={(e) => setNueva({ ...nueva, q: e.target.value, user_id: null })} />
								{personas.length && !nueva.user_id ? (
									<div className="avk-sugerencias">
										{personas.map((p) => <button key={p.id} onClick={() => setNueva({ ...nueva, user_id: p.id, q: p.nombre })}>{p.nombre}<small>{p.correo}</small></button>)}
									</div>
								) : null}
							</div>
						) : (
							<input placeholder="correo@dominio.com" value={nueva.email ?? ""} onChange={(e) => setNueva({ ...nueva, email: e.target.value })} />
						)}
						<select value={nueva.campus_id ?? ""} onChange={(e) => setNueva({ ...nueva, campus_id: e.target.value ? Number(e.target.value) : null })} aria-label="Solo plantel">
							<option value="">Cualquier plantel</option>
							{cat.planteles.map((p) => <option key={p.id} value={p.id}>Solo {p.nombre}</option>)}
						</select>
						{nueva.clase !== "correo" ? (
							<label className="av-check"><input type="checkbox" checked={!!nueva.excluir} onChange={(e) => setNueva({ ...nueva, excluir: e.target.checked })} /> Excluir</label>
						) : null}
						<button className="av-btn pri" disabled={ocupado} onClick={agregar}>Agregar Regla</button>
					</div>
				) : null}
			</section>

			<section className="avk-bloque">
				<div className="avk-bloque-cab">
					<h3>¿Quién lo Recibiría?</h3>
					<div className="avk-vista-filtros">
						<select value={plantel ?? ""} onChange={(e) => setPlantel(e.target.value ? Number(e.target.value) : null)} aria-label="Plantel del evento">
							<option value="">Sin plantel</option>
							{cat.planteles.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
						</select>
						<select value={pais ?? ""} onChange={(e) => setPais(e.target.value || null)} aria-label="País del cliente">
							<option value="">Cualquier país</option><option value="MX">México</option><option value="HN">Honduras</option>
						</select>
						<button className="av-btn chico" disabled={ocupado} onClick={prueba}>Mandarme una Prueba</button>
					</div>
				</div>
				{!vista ? <div className="loader" /> : (
					<>
						{vista.ademas.length ? <div className="avk-ademas">Además, en cada evento: {vista.ademas.join(" · ")}</div> : null}
						{vista.incluidos.length === 0 && !vista.correos.length ? <div className="av-tenue">Nadie fijo en este plantel.</div> : null}
						<ul className="avk-gente">
							{vista.incluidos.map((p) => (
								<li key={p.id}>
									<span className="avk-gente-nom"><b>{p.nombre}</b><small>{p.razones.join(" · ")}</small></span>
									<span className="avk-gente-can">
										{p.apagado ? <em className="oro">Lo apagó</em> : (p.modo === "resumen" ? <em>Resumen</em> : null)}
										{(p.canales || []).map((c) => <i key={c}>{NOMBRE_CANAL[c]}</i>)}
									</span>
								</li>
							))}
							{vista.correos.map((c) => <li key={c}><span className="avk-gente-nom"><b>{c}</b><small>Correo de fuera de WAPP</small></span><span className="avk-gente-can"><i>Correo</i></span></li>)}
						</ul>
						{vista.excluidos.length ? (
							<details className="avk-excluidos">
								<summary>Excluidas ({vista.excluidos.length})</summary>
								<ul className="avk-gente">
									{vista.excluidos.map((p) => <li key={p.id}><span className="avk-gente-nom"><b>{p.nombre}</b><small>{p.razones.join(" · ")}</small></span></li>)}
								</ul>
							</details>
						) : null}
					</>
				)}
			</section>

			<section className="avk-bloque">
				<h3>Últimos Envíos</h3>
				{d.envios.length === 0 ? <div className="av-tenue">Sin envíos todavía.</div> : (
					<ul className="avk-envios">
						{d.envios.map((e) => (
							<li key={e.id} className={e.estado === "fallo" ? "oro" : ""}>
								<span>{e.quien || "—"}</span><span>{(NOMBRE_CANAL as any)[e.canal] || (e.canal === "resumen" ? "Resumen" : e.canal)}</span>
								<span>{e.estado === "ok" ? "Salió" : e.estado === "fallo" ? "Falló" : "Omitido"}</span><span>{cuando(e.creado)}</span>
								{e.detalle && e.estado !== "ok" ? <small>{e.detalle}</small> : null}
							</li>
						))}
					</ul>
				)}
			</section>
		</div>
	);
};

export default ConfigurarAvisos;
